import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, Typography, Divider, CircularProgress,
  TableContainer, Paper, Table, TableHead, TableRow, TableBody, TableCell
} from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/styles';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    width: '70vw',
    maxWidth: 'none',
  },
  root: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  table: {
    width: '100%',
  },
  message: {
    marginTop: theme.spacing(2),
  },
}));

const StyledTableRow = withStyles(theme => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))(TableRow);

const StyledTableCell = withStyles(theme => ({
  head: {
    backgroundColor: 'rgb(60, 179, 113)',
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

const ErpStatusDialog = ({
  open,
  onClose,
  loading,
  data = {},
  TenantId,
  IsHitachiCountry,
  materialList
}) => {
  const { t } = useTranslation('overview');
  const classes = useStyles();

  const hitachiOrders = IsHitachiCountry && Array.isArray(data) ? data : [];
  const nonHitachiOrders = !IsHitachiCountry && Array.isArray(data.out) ? data.out : [];

  const getMaterialCodeByOrderItem = (orderItem) => {
    if (!materialList || !Array.isArray(materialList) || !orderItem) return '';
    const idx = parseInt(orderItem, 10) / 10 - 1;
    const found = materialList.find(m => m.ItemNr === idx);
    return (found && found.MaterialCode) || (materialList[idx] && materialList[idx].MaterialCode) || '';
  };

  function trimLeadingZeros(value) {
    if (value === null || value === undefined) return "";

    const str = String(value).trim();
    const result = str.replace(/^0+/, "");

    return result === "" ? "0" : result;
  }

  const renderHitachi = () => (
    <>
      <Typography variant="h6" align="center" gutterBottom>
        {t('lblHeaderDetails')}
      </Typography>
      <TableContainer component={Paper} style={{ marginBottom: 20 }}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <StyledTableCell align="center">{t('lblSalesOrderNumber')}</StyledTableCell>
              <StyledTableCell align="center">{t('lblSoldToCustomer')}</StyledTableCell>
              <StyledTableCell align="center">{t('lblCustomerReference')}</StyledTableCell>
              <StyledTableCell align="center">{t('lblSalesOrderCreateonDate')}</StyledTableCell>
              <StyledTableCell align="center">{t('lblSoldToCountry')}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {hitachiOrders.map(order => (
              <StyledTableRow key={order.SalesOrderNumber}>
                <StyledTableCell align="center">{order.SalesOrderNumber}</StyledTableCell>
                <StyledTableCell align="center">{order.SoldToCustomer}</StyledTableCell>
                <StyledTableCell align="center">{order.CustomerReference}</StyledTableCell>
                <StyledTableCell align="center">
                  {order.SalesOrderCreateonDate
                    ? moment(order.SalesOrderCreateonDate, 'DD.MM.YYYY').format('DD.MM.YYYY')
                    : ''}
                </StyledTableCell>
                <StyledTableCell align="center">{order.SoldToCountry}</StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" align="center" gutterBottom>
        {t('lblItemDetails')}
      </Typography>
      <TableContainer component={Paper}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              {[
                'lblSalesOrderItem', 'lblMaterialCode', 'lblMaterialDescription',
                'lblSalesOrderQty', 'lblDeliveryQty', 'lblPGIQty', 'lblBilledQty',
                'lblOpenQty', 'lblUnitPrice', 'lblTotalPrice', 'lblReasonRejection',
                'lblDeliveryCreateonDate', 'lblPGIDate', 'lblDays', 'lblOverallStatus',
                'lblCourierNo'
              ].map(labelKey => (
                <StyledTableCell key={labelKey} align="center">{t(labelKey)}</StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {hitachiOrders.flatMap(order =>
              (order.Items || []).map((item, idx) => (
                <StyledTableRow key={`${order.SalesOrderNumber}-${idx}`}>
                  <StyledTableCell align="center">{item.SalesOrderItem}</StyledTableCell>
                  <StyledTableCell align="center">{item.MaterialCode}</StyledTableCell>
                  <StyledTableCell align="center">{item.MaterialDescription}</StyledTableCell>
                  <StyledTableCell align="center">{item.SalesOrderQty}</StyledTableCell>
                  <StyledTableCell align="center">{item.DeliveryQty}</StyledTableCell>
                  <StyledTableCell align="center">{item.PGIQty}</StyledTableCell>
                  <StyledTableCell align="center">{item.BilledQty}</StyledTableCell>
                  <StyledTableCell align="center">{item.OpenQty}</StyledTableCell>
                  <StyledTableCell align="center">{item.UnitPrice}</StyledTableCell>
                  <StyledTableCell align="center">{item.TotalPrice}</StyledTableCell>
                  <StyledTableCell align="center">{item.ReasonRejection}</StyledTableCell>
                  <StyledTableCell align="center">{item.DeliveryCreateonDate}</StyledTableCell>
                  <StyledTableCell align="center">{item.PGIDate}</StyledTableCell>
                  <StyledTableCell align="center">{item.Days}</StyledTableCell>
                  <StyledTableCell align="center">{item.OverallStatus}</StyledTableCell>
                  <StyledTableCell align="center">{item.CourierNo}</StyledTableCell>
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  const renderNonHitachi = () => (
    <>
      {/* --- Non-Hitachi: Out Table --- */}
      <TableContainer component={Paper}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              {[
                'lblOrderNo', 'lblOrderItem', 'lblMaterialCode','lblSubstituteMaterial', 'lblOrderStatus',
                'lblOrderDesc', 'lblOrderDate', 'lblDeliveryNo', 'lblDeliveryStatus',
                'lblDeliveryDate', 'lblInvoiceNo', 'lblInvoiceDate', 'lblGoodIssueDate','lblReasonRejection'
              ].map(labelKey => (
                <StyledTableCell
                  key={labelKey}
                  align={labelKey.endsWith('Date') || labelKey.endsWith('No') ? 'left' : 'center'}
                >
                  {t(labelKey)}
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {nonHitachiOrders.map((s, i) => (
              <StyledTableRow key={`${s.ORDER_NO}-${s.ORDER_ITEM}-${i}`}>
                <StyledTableCell align="center">{s.ORDER_NO}</StyledTableCell>
                <StyledTableCell align="center">{s.ORDER_ITEM}</StyledTableCell>
                <StyledTableCell align="center">
                  {trimLeadingZeros(
                    s.FIRST_MATERIAL && s.FIRST_MATERIAL.trim() !== ""
                      ? s.FIRST_MATERIAL
                      : getMaterialCodeByOrderItem(s.ORDER_ITEM)
                  )}
                </StyledTableCell>
                <StyledTableCell align="center">{trimLeadingZeros(s.SUBSITUTE_MATERIAL)}</StyledTableCell>
                <StyledTableCell align="center">{s.ORDER_STATUS}</StyledTableCell>
                <StyledTableCell align="center">{s.ORDER_DESC}</StyledTableCell>
                <StyledTableCell align="left">
                  {s.ORDER_DATE !== '0000-00-00'
                    ? (TenantId === 79
                        ? moment(s.ORDER_DATE).format('MM/DD/YYYY HH:mm')
                        : moment(s.ORDER_DATE).format('DD/MM/YYYY HH:mm'))
                    : ''}
                </StyledTableCell>
                <StyledTableCell align="left">{s.DELIVERY_NO}</StyledTableCell>
                <StyledTableCell align="left">{s.DELIVERY_STATUS}</StyledTableCell>
                <StyledTableCell align="left">
                  {s.DELIVERY_DATE !== '0000-00-00'
                    ? (TenantId === 79
                        ? moment(s.DELIVERY_DATE).format('MM/DD/YYYY HH:mm')
                        : moment(s.DELIVERY_DATE).format('DD/MM/YYYY HH:mm'))
                    : ''}
                </StyledTableCell>
                <StyledTableCell align="left">{s.INVOICE_NO}</StyledTableCell>
                <StyledTableCell align="left">
                  {s.INVOICE_DATE !== '0000-00-00'
                    ? (TenantId === 79
                        ? moment(s.INVOICE_DATE).format('MM/DD/YYYY HH:mm')
                        : moment(s.INVOICE_DATE).format('DD/MM/YYYY HH:mm'))
                    : ''}
                </StyledTableCell>
                <StyledTableCell align="left">
                  {s.GOOD_ISSUE_DATE !== '0000-00-00'
                    ? (TenantId === 79
                        ? moment(s.GOOD_ISSUE_DATE).format('MM/DD/YYYY HH:mm')
                        : moment(s.GOOD_ISSUE_DATE).format('DD/MM/YYYY HH:mm'))
                    : ''}
                </StyledTableCell>
                <StyledTableCell align="left">{s.REASON_REJECTION}</StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{ classes: { root: classes.dialogPaper } }}
      aria-labelledby="erp-status-dialog-title"
    >
      <DialogTitle id="erp-status-dialog-title">
        <Typography variant="h3">{t('lblSparePartorderDetails')}</Typography>
        <Divider />
      </DialogTitle>

      <DialogContent className={classes.root}>
        {!loading ?   (
          (IsHitachiCountry ? (
            hitachiOrders.length > 0 ? renderHitachi() : (
              <Typography variant="body1" align="center" className={classes.message}>
                {t('lblErpMsg')}
              </Typography>
            )
          ) : (
            nonHitachiOrders.length > 0 ? renderNonHitachi() : (
              <Typography variant="body1" align="center" className={classes.message}>
                {t('lblErpMsg')}
              </Typography>
            )
          ))
        ) :'' }
      </DialogContent>
    </Dialog>
  );
};

export default ErpStatusDialog;
